import { ErrorResponseBody } from '@/@types/common'
import useUniversalNavigate from '@/utils/hooks/useUniversalNavigate'
import { css } from '@emotion/react'
import { useQueryErrorResetBoundary } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { type HTMLAttributes, useEffect, useState } from 'react'
import { z } from 'zod'
import Button from './Button'

const InfoSchema = z.object({
	title: z.string(),
	desc: z.string(),
})
type Info = z.infer<typeof InfoSchema>

export interface ErrorBoundaryProps extends HTMLAttributes<HTMLDivElement> {
	error: Error
	reset: () => void
}

export default function ErrorBoundary({ error, reset, ...props }: ErrorBoundaryProps) {
	const queryErrorResetBoundary = useQueryErrorResetBoundary()
	const navigate = useUniversalNavigate()
	const [info, setInfo] = useState<Info>({
		title: 'Something went wrong',
		desc: error.message,
	})

	useEffect(() => {
		queryErrorResetBoundary.reset()
	}, [queryErrorResetBoundary])

	useEffect(() => {
		if (isAxiosError<ErrorResponseBody>(error)) {
			if (error.response?.data.error) {
				setInfo({
					title: error.response?.data.error || 'Something went wrong',
					desc: error.message,
				})
			}
		}
	}, [error])

	return (
		<div
			{...props}
			css={css`
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: center;
				gap: 16px;
				height: 100vh;
			`}
		>
			<div
				css={css`
					font-size: 24px;
					line-height: 1.5;
					text-align: center;
				`}
			>
				{info.title}
				<br />
				<div
					css={css`
						font-size: 16px;
						color: var(--tint-secondary);
					`}
				>
					{info.desc}
				</div>
			</div>
			<div
				css={css`
					display: flex;
					gap: 10px;
					align-items: center;
				`}
			>
				<Button
					appearance="ghost"
					size="sm"
					onClick={() => {
						reset()
					}}
				>
					重试
				</Button>
				<Button size="sm" onClick={(e) => navigate({ to: '/' }, e)}>
					返回主页
				</Button>
			</div>
		</div>
	)
}
